import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../../Shared/api';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Button } from '../../../Shared/UI/Button';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { InputWrapper } from '../../../Shared/UI/InputWrapper';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useCurrencyInput } from '../../../Shared/useCurrencyInput';
import { useRouter } from '../../router';

export default function AddProductForm({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { handleCurrencyBlur, parseCurrency } = useCurrencyInput();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      title: '',
      selling_price: '',
      enabled: false,
    },
  });

  const {
    register, handleSubmit, setError, setValue,
    formState: { errors, defaultValues },
  } = form;

  const [addProduct, { loading, errors: serverErrors }] = useMutation(
    routes.add_product(),
    {
      onSuccess: () => {
        notifier.notify(t('backend:product_created'));
        onSuccess?.();
      },
      onError: (errors) => {
        setServerErrors(errors, setError, defaultValues);
      },
    }
  );

  const submit = async (data: typeof defaultValues) => {
    await addProduct({
      ...data,
      selling_price: data?.selling_price ? parseCurrency(data.selling_price) : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_product')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_product_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel required valid={!errors.title} htmlFor="add_product_title">
            {t('backend:product_title')}
          </InputLabel>
          <Input
            {...register('title', { required: true })}
            id="add_product_title"
            className="mr-4"
            aria-invalid={!!errors.title}
          />
          <ErrorMessage attribute={t('backend:product_title')} error={errors.title }/>
        </InputGroup>

        <InputGroup>
          <InputLabel required valid={!errors.title} htmlFor="add_product_cost_price">
            {t('backend:selling_price')}
          </InputLabel>
          <InputWrapper>
            <label htmlFor="add_product_cost_price">
              €
            </label>
            <Input
              {...register('selling_price', { required: true })}
              id="add_product_cost_price"
              className="pl-7"
              onBlur={(event) => {
                handleCurrencyBlur(event);
                setValue('selling_price', event.target.value);
              }}
              aria-invalid={!!errors.selling_price}
              inputMode="decimal"
            />
          </InputWrapper>
          <ErrorMessage error={errors.selling_price} attribute={t('backend:selling_price')} />
        </InputGroup>

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:continue')}
            <Icon className="ml-1">
              <ChevronRight />
            </Icon>
          </Button>
        </div>
      </div>
    </form>
  );
}
